import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "@/lib/api";

const STATUSES = ["", "new", "contacted", "quoted", "won", "lost"];
const TYPES = ["", "Handyman Repair", "Remodeling", "Other"];
const RANGES = [{ v: 0, l: "All time" }, { v: 7, l: "Last 7 days" }, { v: 30, l: "Last 30 days" }];
const badge = {
  new: "bg-orange/10 text-orange border-orange/30",
  contacted: "bg-blue-50 text-blue-700 border-blue-200",
  quoted: "bg-amber-50 text-amber-700 border-amber-200",
  won: "bg-green-50 text-green-700 border-green-200",
  lost: "bg-red-50 text-red-600 border-red-200",
};

export default function Estimates() {
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  const [days, setDays] = useState(0);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-estimates", status, type, days],
    queryFn: async () =>
      (await api.get("/admin/estimates", {
        params: { ...(status && { status }), ...(type && { project_type: type }), ...(days && { days }) },
      })).data,
  });

  const selectCls = "border border-sandline bg-white px-3 py-2 text-sm focus:outline-none focus:border-orange";

  return (
    <div data-testid="admin-estimates">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <h1 className="font-heading font-bold text-2xl tracking-tight">Estimates Inbox</h1>
        <div className="flex flex-wrap gap-3">
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="filter-status" className={selectCls}>
            {STATUSES.map((s) => <option key={s} value={s}>{s ? s[0].toUpperCase() + s.slice(1) : "All statuses"}</option>)}
          </select>
          <select value={type} onChange={(e) => setType(e.target.value)} data-testid="filter-type" className={selectCls}>
            {TYPES.map((tp) => <option key={tp} value={tp}>{tp || "All types"}</option>)}
          </select>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="filter-date" className={selectCls}>
            {RANGES.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
          </select>
        </div>
      </div>

      <div className="bg-white border border-sandline overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]" data-testid="estimates-table">
          <thead>
            <tr className="border-b border-sandline text-left">
              {["Date", "Name", "Type", "Phone", "Status", ""].map((h) => (
                <th key={h} className="px-5 py-4 text-xs font-bold uppercase tracking-widest text-slateblue">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={6} className="px-5 py-10 text-center text-slateblue">Loading…</td></tr>
            ) : (data || []).length === 0 ? (
              <tr><td colSpan={6} className="px-5 py-10 text-center text-slateblue" data-testid="estimates-empty">No estimates match these filters.</td></tr>
            ) : (
              data.map((e) => (
                <tr key={e.id} className="border-b border-offwhite-dark hover:bg-offwhite/60 transition-colors duration-150" data-testid={`estimate-row-${e.id.slice(0, 8)}`}>
                  <td className="px-5 py-4 text-slateblue whitespace-nowrap">{new Date(e.created_at).toLocaleDateString()}</td>
                  <td className="px-5 py-4 font-semibold">{e.name}</td>
                  <td className="px-5 py-4">{e.project_type}</td>
                  <td className="px-5 py-4 text-slateblue">{e.phone}</td>
                  <td className="px-5 py-4">
                    <span className={`inline-block px-2.5 py-1 text-xs font-bold uppercase tracking-wide border ${badge[e.status] || badge.new}`}>{e.status}</span>
                  </td>
                  <td className="px-5 py-4 text-right">
                    <Link to={`/admin/estimates/${e.id}`} data-testid={`estimate-view-${e.id.slice(0, 8)}`} className="text-orange font-bold text-xs uppercase tracking-widest hover:text-orange-bright">View</Link>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
